/**
 * Error Categorization Utility
 *
 * Provides user-friendly error messages and categorizes errors
 * for appropriate UI handling and retry decisions.
 */

export type ErrorCategory =
  | 'network'      // Connection/network errors - usually retryable
  | 'timeout'      // Request timed out - usually retryable
  | 'rate_limit'   // Rate limiting - wait and retry
  | 'token_limit'  // Content too large - not retryable without changes
  | 'server'       // Server error (5xx) - usually retryable
  | 'auth'         // Authentication error - not retryable
  | 'validation'   // Invalid input - not retryable without changes
  | 'unknown';     // Unknown error - may be retryable

export interface CategorizedError {
  category: ErrorCategory;
  userMessage: string;
  technicalMessage: string;
  isRetryable: boolean;
  suggestedAction?: string;
}

/**
 * Categorize an error and return user-friendly messaging
 */
export function categorizeError(error: unknown): CategorizedError {
  const errorMessage = error instanceof Error
    ? error.message
    : typeof error === 'string'
      ? error
      : 'Unknown error';

  const lowerMessage = errorMessage.toLowerCase();

  // Network/Connection errors
  if (
    lowerMessage.includes('network') ||
    lowerMessage.includes('exception posting') ||
    lowerMessage.includes('econnreset') ||
    lowerMessage.includes('socket hang up') ||
    lowerMessage.includes('enotfound') ||
    lowerMessage.includes('connection') ||
    lowerMessage.includes('failed to fetch')
  ) {
    return {
      category: 'network',
      userMessage: 'Connection error occurred',
      technicalMessage: errorMessage,
      isRetryable: true,
      suggestedAction: 'Please check your internet connection and try again.'
    };
  }

  // Timeout errors
  if (
    lowerMessage.includes('timeout') ||
    lowerMessage.includes('etimedout') ||
    lowerMessage.includes('timed out')
  ) {
    return {
      category: 'timeout',
      userMessage: 'Request timed out',
      technicalMessage: errorMessage,
      isRetryable: true,
      suggestedAction: 'The server is taking too long to respond. Please try again.'
    };
  }

  // Rate limiting
  if (
    lowerMessage.includes('rate limit') ||
    lowerMessage.includes('429') ||
    lowerMessage.includes('quota exceeded') ||
    lowerMessage.includes('too many requests')
  ) {
    return {
      category: 'rate_limit',
      userMessage: 'Service is temporarily busy',
      technicalMessage: errorMessage,
      isRetryable: true,
      suggestedAction: 'Please wait a moment and try again.'
    };
  }

  // Token/Content limit errors
  if (
    lowerMessage.includes('token') ||
    lowerMessage.includes('too large') ||
    lowerMessage.includes('exceeds') ||
    lowerMessage.includes('maximum') ||
    lowerMessage.includes('content length')
  ) {
    return {
      category: 'token_limit',
      userMessage: 'Content too large to process',
      technicalMessage: errorMessage,
      isRetryable: false,
      suggestedAction: 'Try using fewer documents or smaller files.'
    };
  }

  // Server errors (5xx)
  if (
    lowerMessage.includes('500') ||
    lowerMessage.includes('502') ||
    lowerMessage.includes('503') ||
    lowerMessage.includes('504') ||
    lowerMessage.includes('internal server error') ||
    lowerMessage.includes('service unavailable')
  ) {
    return {
      category: 'server',
      userMessage: 'Server error occurred',
      technicalMessage: errorMessage,
      isRetryable: true,
      suggestedAction: 'The server encountered an error. Please try again.'
    };
  }

  // Authentication errors
  if (
    lowerMessage.includes('401') ||
    lowerMessage.includes('403') ||
    lowerMessage.includes('unauthorized') ||
    lowerMessage.includes('forbidden') ||
    lowerMessage.includes('authentication')
  ) {
    return {
      category: 'auth',
      userMessage: 'Authentication error',
      technicalMessage: errorMessage,
      isRetryable: false,
      suggestedAction: 'Please log in again and retry.'
    };
  }

  // Validation errors
  if (
    lowerMessage.includes('400') ||
    lowerMessage.includes('invalid') ||
    lowerMessage.includes('validation') ||
    lowerMessage.includes('required')
  ) {
    return {
      category: 'validation',
      userMessage: 'Invalid request',
      technicalMessage: errorMessage,
      isRetryable: false,
      suggestedAction: 'Please check your input and try again.'
    };
  }

  // Default - unknown error
  return {
    category: 'unknown',
    userMessage: 'An error occurred',
    technicalMessage: errorMessage,
    isRetryable: true,
    suggestedAction: 'Please try again. If the problem persists, contact support.'
  };
}

/**
 * Get a concise error message suitable for UI display
 */
export function getDisplayErrorMessage(error: unknown): string {
  const categorized = categorizeError(error);
  return categorized.userMessage;
}

/**
 * Get a detailed error message with suggested action
 */
export function getDetailedErrorMessage(error: unknown): string {
  const categorized = categorizeError(error);
  return `${categorized.userMessage}. ${categorized.suggestedAction || ''}`.trim();
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  return categorizeError(error).isRetryable;
}

/**
 * Format an error for logging (includes technical details)
 */
export function formatErrorForLogging(error: unknown): string {
  const categorized = categorizeError(error);
  return `[${categorized.category.toUpperCase()}] ${categorized.technicalMessage}`;
}
